// Mobile-only disclosure for filter toolbars.
//
// Below the `sm` breakpoint the wrapped controls collapse behind a single
// "Filter" toggle (with an active-filter badge) so task pages open with one
// compact toolbar row instead of a full screen of stacked selects. From `sm`
// up the wrapper renders as display:contents, so the controls sit inline in
// the parent flex row exactly as before — desktop layout is unchanged.
//
// The controls stay in the DOM when collapsed (CSS `hidden`), so tests and
// screen-reader label queries keep working and no state is lost on toggle.

import { ChevronDown, SlidersHorizontal } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { useId, useState } from "preact/hooks";
import type { ComponentType } from "preact";

interface Props {
  children: ComponentChildren;
  /** Number of active filters — shown as a badge on the toggle when > 0. */
  count?: number;
  /** Toggle label; defaults to "Filter". */
  label?: string;
  /**
   * UX-Vorschlag P7: unter 768 px (statt 640 px) öffnet der Knopf dieses
   * Bottom-Sheet (components/BottomSheet) statt die Steuerelemente in der
   * Seite aufzuklappen. Als Komponente hereingereicht statt importiert, damit
   * dieses Bauteil kein preact/compat (Portal) nachlädt. Ohne Prop: wie bisher.
   */
  sheet?: ComponentType<{ title: string; onClose: () => void; children: ComponentChildren }>;
}

export function FilterDisclosure(props: Props): preact.JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const count = props.count ?? 0;

  const Sheet = props.sheet;
  if (Sheet) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          class="md:hidden inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[36px] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
        >
          <SlidersHorizontal size={14} aria-hidden="true" />
          {props.label ?? "Filter"}
          {count > 0 && (
            <span class="inline-flex items-center justify-center rounded-full bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] text-label font-semibold min-w-[16px] h-[16px] px-1">
              {count}
            </span>
          )}
        </button>
        {open ? (
          <Sheet title={props.label ?? "Filter"} onClose={() => setOpen(false)}>
            <div class="flex flex-col items-stretch gap-3">{props.children}</div>
          </Sheet>
        ) : (
          <div class="hidden md:contents">{props.children}</div>
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        class="sm:hidden inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface-variant)] min-h-[36px] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
      >
        <SlidersHorizontal size={14} aria-hidden="true" />
        {props.label ?? "Filter"}
        {count > 0 && (
          <span class="inline-flex items-center justify-center rounded-full bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] text-label font-semibold min-w-[16px] h-[16px] px-1">
            {count}
          </span>
        )}
        <ChevronDown
          size={14}
          class={`transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <div
        id={panelId}
        class={`${open ? "flex" : "hidden"} w-full flex-col items-stretch gap-2 sm:contents`}
      >
        {props.children}
      </div>
    </>
  );
}
